import React, { useState, useEffect, useMemo} from 'react'
import { Form, Button } from 'antd'
import { Filters } from '@rankmi/rankmi-table'
import { filtersLocale } from '@frontend/src/utilities/filter_config';
import { ListService } from '@services/list_service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import styles from './FilterForm.module.scss'
import { set } from 'js-cookie';

const reference = {
  eq: 1,
  not_eq: 3,
  gt: 2,
  lt: 0,
  cont: 4,
  not_cont: 5,
  null: 6,
  not_null: 7,
  start: 8,
  end: 9,
  true: 10,
  false: 11
};


const stringFilterOptions = [
  {
    label: "Contiene",
    value: "cont",
  },
  {
    label: "No contiene",
    value: "not_cont",
  },
  {
    label: "Es igual a",
    value: "eq",
  },
  {
    label: "No es",
    value: "not_eq",
  },
  {
    label: "Es mayor que",
    value: "gt",
  },
  {
    label: "Es menor que",
    value: "lt",
  },
];

const numberFilterOptions = [
  {
    label: "Es igual a",
    value: "eq",
  },
  {
    label: "No es",
    value: "not_eq",
  },
  {
    label: "Es mayor que",
    value: "gt",
  },
  {
    label: "Es menor que",
    value: "lt",
  },
];

const booleanFilterOptions = [
  {
    label: "Es verdadero",
    value: "true",
  },
  {
    label: "Es falso",
    value: "false",
  },
];

const filtersOptionsTest = [
  {
    label: "Mettl",
    value: "mettl"
  }
]

const filterOptionsExternal = [
  {
    label: "Es",
    value: "mettl"
  }
]

const excludeOperators = ["null", "not_null", "cont", "not_cont", "gteq", "lteq" ]

const NUMBER_OPERATORS = ["eq", "not_eq", "gt", "lt"]
const BOOLEAN_OPERATORS = ["true", "false"]

const operatorFromCompare = (compare) =>
  Object.keys(reference).find((key) => reference[key] === compare)

// rankmi-table reads operatorData.value without a guard; a stored
// text operator on a number/boolean column throws when opening the filter.
const hydrateOperator = (operator, fieldType) => {
  if (!operator) return operator
  if (fieldType === "number") {
    if (NUMBER_OPERATORS.includes(operator)) return operator
    if (operator === "not_cont") return "not_eq"
    return "eq"
  }
  if (fieldType === "boolean") {
    if (BOOLEAN_OPERATORS.includes(operator)) return operator
    if (operator === "not_cont" || operator === "not_eq") return "false"
    return "true"
  }
  return operator
}

const FilterModal = ({value, rules, readonly=false, hybridSearch, hybridOptions}) => {
  const [openFilter, setOpenFilters] = useState(false)
  const [data, setData] = useState({})
  const [chargingData, setChargingData] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([])
  const service = new ListService

  useEffect(()=> {
    setChargingData(true)
    service.fetchRules(value.id, (data) => {
      const found = data.rules.find(elem => elem.element === 'university')
      if(found) {
        hybridSearch.universities(found.value)
      }
      setData(data)
      setChargingData(false)
    }, console.error)
  }, [])

  useEffect(() => {
    setSelectedFilters(getSelectedFilters());
  }, [data])

  function isStringNumber(str) {
    return !Number.isNaN(Number(str));
  }

  const getCatalogType = (element) => {
    const entry = Object.values(rules).find((rule) => rule.value === element)
    if (!entry) return null
    return translateTypes(entry.type)
  }

  const resolveFilterType = (elem, catalogType) => {
    if (catalogType === "boolean" || catalogType === "number") return catalogType
    if (elem.category === "value") {
      return isStringNumber(elem.value) ? "number" : "string"
    }
    return "list"
  }

  const getSelectedFilters = () => {
    if(Object.keys(data).length > 0) {
      return Object.entries(data).length !== 0
        ? {
            generalCondition: data.global_comparer,
            filterType: "simple",
            data: data.rules.map((elem) => {
              if(elem.category === 'test') {
                const testValue = elem.element.split('&&')
                const childType = Number(elem.value) ? "number" : "list"
                return {
                  id: elem.id,
                  input: 'tests',
                  type: 'group',
                  operator: 'mettl',
                  compareTo: Number(testValue[1]),
                  childrensGroup: {
                    input: testValue[0],
                    type: childType,
                    operator: hydrateOperator(
                      operatorFromCompare(elem.compare),
                      childType
                    ),
                    compareTo: elem.value,
                  }
                }
              }
              const catalogType = getCatalogType(elem.element)
              const type = resolveFilterType(elem, catalogType)
              return {
                id: elem.id,
                input: elem.element,
                type,
                operator: hydrateOperator(
                  operatorFromCompare(elem.compare),
                  catalogType || type
                ),
                compareTo: elem.value,
            }}),
          }
        : null;
      }
      return null;
  }

  const translateTypes = (value) => {
    if (value === 'text') return 'string'

    return value
  }

  const columns = useMemo(() => {
    const aux = []
    Object.keys(rules).forEach((value) => {
      if (rules[value]["type"] === "list") {
        aux.push({
          label: value,
          value: rules[value].value,
          type: "list",
          excludedOperators: excludeOperators,
          list: rules[value].options.map((elem) => ({
            label: elem.label,
            value: elem.value,
          })),
        });
      } else if (rules[value]["type"] === "group")  {
        aux.push({
          label: value,
          value: rules[value].value,
          type: "group",
          filterOptions: rules[value]["externalTests"] ? filterOptionsExternal : filtersOptionsTest,
          list: rules[value].options.map((elem) => ({
            label: elem.label,
            value: elem.value,
            childrensGroup: elem.childrensGroup.map(dimention => {
              let value = {
                label: dimention.label,
                value: dimention.label,
                type: dimention.type,
                excludedOperators: excludeOperators,
                min: 0
              }
              if (dimention.type === 'list') {
                value = {
                  ...value,
                  list: dimention.list.map((item) => ({
                    label: item.label,
                    value: item.label,
                    type: item.type
                  }))
                }
              }
              return value
            })
          }))
        });
      }
      else if (rules[value]["type"] === "hybrid") {
        aux.push({
          label: "Universidad",
          value: "university",
          type: "hybrid",
          list: hybridOptions.universities,
          searchStringKey: "university.label",
          onSearch: (text) => { hybridSearch.universities(text) }
        })
      }
      else {
        const type = translateTypes(rules[value]["type"])
        aux.push({
          label: value,
          value: rules[value].value,
          type,
          filterOptions: type === "number"
            ? numberFilterOptions
            : type === "boolean"
              ? booleanFilterOptions
              : stringFilterOptions
        })
      }
    });

  return aux
  }, [rules, hybridOptions]);

  const createRules = (rules) => {
    const request = {
      list: {
        global_comparer: rules["generalCondition"],
        rules_attributes: rules["data"].map((elem) => {

        if(elem.input === "tests") {
          return {
            id: elem.id,
            value: elem.childrensGroup.type !== 'number'
            ? elem.childrensGroup.compareTo
            : Number(elem.childrensGroup.compareTo),
            compare: reference[elem.childrensGroup.operator],
            element: elem.childrensGroup.input + `&&${elem.compareTo}`,
            category: "test"
          }
        }
        else if (elem.input === "university") {
          return {
            id: elem.id,
            value: elem.compareTo.toString(),
            compare: reference[elem.operator],
            element: "university",
            category: "university"
          }
        }
        else {
          return {
            id: elem.id,
            value: elem["compareTo"],
            compare: reference[elem["operator"]],
            element: elem["input"],
            category: ["string", "number"].includes(elem["type"]) ? "value" : "option"
          }}
        })
      },
    };
    service.createRule(
      value.id,
      request,
      setData,
      console.error
    );
  };

  return(
    <Form.Item
      label={<div className='filter-item-label'>{value.name}:</div>}
      colon={false}
    >
      {readonly ? (
        <Button
          className={`${styles.filterButton} rankmiTable-filters-button`}
          disabled={true}
        >
          <FontAwesomeIcon className="mr-2" icon={faFilter} />
          Filtrar
        </Button>
      ) : (
        <Filters
          open={openFilter}
          onVisibilityChange={(state) => setOpenFilters(state)}
          columns={columns}
          onlySimpleFilters
          locale={filtersLocale}
          selectedFilters={selectedFilters}
          onChange={(res)=> createRules(res)}
          loading={chargingData}
          readonly={readonly}
        />
      )}
    </Form.Item>
  )
}

export default FilterModal
